import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Megaphone, Building2, Phone, Radio, Film, Globe, ArrowRight, Play, ChevronRight, 
  Mic2, Headphones, Target, Zap, Handshake, Globe2, Lightbulb, Quote, Star
} from 'lucide-react';
import { siteConfig, services, featuredDemos } from '../data/mock';
import AudioPlayer from '../components/ui/AudioPlayer';

const iconMap = {
  Megaphone: Megaphone,
  Building2: Building2,
  Phone: Phone,
  Radio: Radio,
  Film: Film,
  Globe: Globe,
};

// Brand logos data
const brandLogos = [
  { name: 'UNICEF', src: 'https://customer-assets.emergentagent.com/job_african-voice-pro/artifacts/8g88rq8k_UNICEF-Logo-1645549223.png' },
  { name: 'Cadbury', src: 'https://customer-assets.emergentagent.com/job_african-voice-pro/artifacts/v4zjoeum_Cadbury_GlobalNavigation_BrandLogo_Desktop.svg' },
  { name: 'Coca-Cola', src: 'https://customer-assets.emergentagent.com/job_african-voice-pro/artifacts/576r2b55_cocacola_logo_PNG11.png' },
  { name: 'Nivea', src: 'https://customer-assets.emergentagent.com/job_african-voice-pro/artifacts/ztepoqsg_logo-nivea-4096.png' },
];

// Testimonials data
const testimonials = [
  {
    id: 1,
    quote: "Joy's voice brought our brand story to life in ways we never imagined. Her professionalism and attention to detail made the entire process seamless.",
    name: "Adaeze Okonkwo",
    role: "Marketing Director",
    company: "Leading FMCG Brand"
  },
  {
    id: 2,
    quote: "Working with Joy was an absolute pleasure. Her ability to understand our brand tone and deliver exactly what we needed was remarkable.",
    name: "Michael Chen",
    role: "Creative Producer",
    company: "International Agency"
  },
  {
    id: 3,
    quote: "The documentary narration Joy provided elevated our entire production. Her voice commands attention while remaining warm and accessible.",
    name: "Olumide Adesanya",
    role: "Documentary Filmmaker",
    company: "African Stories Media"
  }
];

const HomePage = () => {
  return (
    <div className="min-h-screen bg-[#0D0D0D] overflow-hidden">
      {/* Hero Section - Full Screen Cinematic */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0">
          <img
            src={siteConfig.heroImage}
            alt="Joy Ogbekene"
            className="w-full h-full object-cover object-top opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0D0D0D]/60 via-[#0D0D0D]/80 to-[#0D0D0D]" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0D0D0D]/90 via-transparent to-[#0D0D0D]/90" />
        </div>

        {/* Hero Content */}
        <div className="relative z-10 max-w-5xl mx-auto px-6 lg:px-8 text-center pt-20">
          <div className="animate-fade-in-up opacity-0" style={{ animationDelay: '0.1s', animationFillMode: 'forwards' }}>
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-[#C9A962]/10 border border-[#C9A962]/20 rounded-full text-[#C9A962] text-sm mb-8">
              <span className="w-2 h-2 bg-[#C9A962] rounded-full animate-pulse" />
              20+ Years of Professional Experience
            </span>
          </div>

          <h1 className="animate-fade-in-up opacity-0 heading-hero mb-6" style={{ animationDelay: '0.2s', animationFillMode: 'forwards' }}>
            A Voice That Commands Trust<br />
            <span className="text-[#C9A962]">And Connects Across Cultures</span>
          </h1>

          <p className="animate-fade-in-up opacity-0 text-xl md:text-2xl text-white/90 font-light mb-4" style={{ animationDelay: '0.3s', animationFillMode: 'forwards' }}>
            Helping Brands sound credible, relatable, and unforgettable.
          </p>

          <p className="animate-fade-in-up opacity-0 text-[#B8B8B8] text-base mb-4" style={{ animationDelay: '0.35s', animationFillMode: 'forwards' }}>
            Professional Nigerian & African Female Voice Artist
          </p>
          
          <p className="animate-fade-in-up opacity-0 text-[#707070] text-sm tracking-widest uppercase mb-12" style={{ animationDelay: '0.4s', animationFillMode: 'forwards' }}>
            Commercial • Corporate • IVR • Documentary • Promo
          </p>

          <div className="animate-fade-in-up opacity-0 flex flex-col sm:flex-row items-center justify-center gap-4" style={{ animationDelay: '0.5s', animationFillMode: 'forwards' }}>
            <Link
              to="/demos"
              className="group inline-flex items-center gap-3 px-8 py-4 bg-[#C9A962] text-[#0D0D0D] font-medium rounded-full hover:bg-[#E5D4A1] transition-all duration-300 hover:shadow-[0_0_30px_rgba(201,169,98,0.3)]"
            >
              <Play size={18} />
              Listen to Demos
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-8 py-4 border border-[#2A2A2A] text-white font-medium rounded-full hover:border-[#C9A962] hover:text-[#C9A962] transition-all duration-300"
            >
              Book a Session
            </Link>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-[#2A2A2A] rounded-full flex items-start justify-center p-2">
            <div className="w-1.5 h-3 bg-[#C9A962] rounded-full" />
          </div>
        </div>
      </section>

      {/* Animated Trust Strip */}
      <section className="py-12 border-y border-[#1A1A1A] bg-[#0A0A0A] overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <p className="text-center text-[#505050] text-sm uppercase tracking-widest mb-8">
            Trusted by Nigerian, African, and International Brands
          </p>
        </div>
        
        {/* Scrolling logos container */}
        <div className="relative">
          <div className="flex animate-scroll-left">
            {/* First set */}
            <div className="flex items-center gap-16 px-8 shrink-0">
              {brandLogos.map((logo, index) => (
                <img 
                  key={`first-${index}`}
                  src={logo.src} 
                  alt={logo.name} 
                  className="h-10 md:h-12 w-auto object-contain opacity-60 hover:opacity-100 transition-opacity duration-300"
                />
              ))}
            </div>
            {/* Duplicate set for seamless loop */}
            <div className="flex items-center gap-16 px-8 shrink-0">
              {brandLogos.map((logo, index) => (
                <img 
                  key={`second-${index}`}
                  src={logo.src} 
                  alt={logo.name} 
                  className="h-10 md:h-12 w-auto object-contain opacity-60 hover:opacity-100 transition-opacity duration-300"
                />
              ))}
            </div>
            {/* Third set for extra smoothness */}
            <div className="flex items-center gap-16 px-8 shrink-0">
              {brandLogos.map((logo, index) => (
                <img 
                  key={`third-${index}`}
                  src={logo.src} 
                  alt={logo.name} 
                  className="h-10 md:h-12 w-auto object-contain opacity-60 hover:opacity-100 transition-opacity duration-300"
                />
              ))}
            </div>
          </div>
          {/* Fade edges */}
          <div className="absolute left-0 top-0 bottom-0 w-20 bg-gradient-to-r from-[#0A0A0A] to-transparent pointer-events-none" />
          <div className="absolute right-0 top-0 bottom-0 w-20 bg-gradient-to-l from-[#0A0A0A] to-transparent pointer-events-none" />
        </div>
      </section>

      {/* Services Section with Enhanced Cards */}
      <section className="py-24 bg-[#0D0D0D] relative">
        {/* Background accent */}
        <div className="absolute top-0 right-0 w-1/2 h-1/2 bg-[#C9A962]/5 blur-[150px] rounded-full pointer-events-none" />
        
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <span className="text-[#C9A962] text-sm uppercase tracking-widest">Services</span>
            <h2 className="heading-1 mt-4">Voiceover Services</h2>
            <p className="body-large mt-4 max-w-2xl mx-auto">
              Professional voice solutions tailored to your brand's unique needs
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => {
              const IconComponent = iconMap[service.icon];
              return (
                <Link
                  key={service.id}
                  to="/demos"
                  className="group relative bg-gradient-to-b from-[#1A1A1A] to-[#161616] border border-[#1A1A1A] rounded-2xl p-8 hover:border-[#C9A962]/40 transition-all duration-500 overflow-hidden"
                >
                  {/* Hover glow */}
                  <div className="absolute inset-0 bg-gradient-to-b from-[#C9A962]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  
                  <div className="relative z-10">
                    <div className="w-14 h-14 bg-gradient-to-br from-[#C9A962]/20 to-[#C9A962]/5 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                      {IconComponent && <IconComponent size={28} className="text-[#C9A962]" />}
                    </div>
                    <h3 className="heading-3 mb-3 group-hover:text-[#C9A962] transition-colors">
                      {service.title}
                    </h3>
                    <p className="body-small text-[#707070]">{service.description}</p>
                    <div className="mt-6 flex items-center gap-2 text-[#505050] group-hover:text-[#C9A962] transition-colors">
                      <span className="text-sm font-medium">Listen to Samples</span>
                      <ArrowRight size={16} className="group-hover:translate-x-2 transition-transform" />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* About Preview Section */}
      <section className="py-24 bg-gradient-to-b from-[#0D0D0D] to-[#0A0A0A] relative">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Image */}
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden">
                <img
                  src={siteConfig.heroImage}
                  alt="Joy Ogbekene"
                  className="w-full aspect-[4/5] object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0D0D0D]/60 via-transparent to-transparent" />
              </div>
              {/* Decorative Elements */}
              <div className="absolute -bottom-6 -right-6 w-48 h-48 border border-[#C9A962]/20 rounded-2xl -z-10" />
              <div className="absolute -top-6 -left-6 w-32 h-32 bg-[#C9A962]/5 rounded-2xl -z-10" />
            </div>

            {/* Content */}
            <div>
              <span className="text-[#C9A962] text-sm uppercase tracking-widest">About Joy</span>
              <h2 className="heading-1 mt-4 mb-6">Experience You Can Hear</h2>
              <div className="space-y-4">
                <p className="body-large">
                  Joy Ogbekene is a professional Nigerian and African voice artist with 
                  over 20 years of experience delivering compelling voiceovers across 
                  continents, cultures, and platforms.
                </p>
                <p className="body-medium text-[#707070]">
                  From commercials and corporate narrations to IVR systems, promos, and 
                  documentaries, her voice brings clarity, warmth, and credibility to 
                  every script.
                </p>
                <p className="body-medium text-[#707070]">
                  With a versatile range spanning neutral English, African-accented 
                  English, and Nigerian Pidgin, Joy helps brands communicate with 
                  authenticity—locally and globally.
                </p>
              </div>
              <Link
                to="/about"
                className="inline-flex items-center gap-2 mt-8 text-[#C9A962] hover:text-[#E5D4A1] transition-colors font-medium"
              >
                Learn More About Joy
                <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Why Brands Choose Joy - Persuasion Block */}
      <section className="py-24 bg-[#0A0A0A] relative">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNDOUE5NjIiIGZpbGwtb3BhY2l0eT0iMC4wMyI+PGNpcmNsZSBjeD0iMzAiIGN5PSIzMCIgcj0iMSIvPjwvZz48L2c+PC9zdmc+')] opacity-50" />
        
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <span className="text-[#C9A962] text-sm uppercase tracking-widest">The Difference</span>
            <h2 className="heading-1 mt-4">Why Brands Choose Joy Ogbekene</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Card 1 */}
            <div className="bg-[#161616] border border-[#1A1A1A] rounded-2xl p-8 hover:border-[#C9A962]/30 transition-all duration-300 hover:-translate-y-1">
              <div className="w-12 h-12 bg-[#C9A962]/10 rounded-xl flex items-center justify-center mb-6">
                <Mic2 size={24} className="text-[#C9A962]" />
              </div>
              <h3 className="heading-3 mb-3">More Than a Voice: A Voice Interpreter</h3>
              <p className="body-small">
                Joy doesn't just read scripts; she understands intent, emotion, and audience 
                psychology, delivering performances that communicate meaning, not just words.
              </p>
            </div>

            {/* Card 2 */}
            <div className="bg-[#161616] border border-[#1A1A1A] rounded-2xl p-8 hover:border-[#C9A962]/30 transition-all duration-300 hover:-translate-y-1">
              <div className="w-12 h-12 bg-[#C9A962]/10 rounded-xl flex items-center justify-center mb-6">
                <Headphones size={24} className="text-[#C9A962]" />
              </div>
              <h3 className="heading-3 mb-3">Versatility That Fits Your Brand</h3>
              <p className="body-small">
                From warm and conversational to authoritative and energetic, her delivery 
                adapts seamlessly to your brand's tone and objectives.
              </p>
            </div>

            {/* Card 3 */}
            <div className="bg-[#161616] border border-[#1A1A1A] rounded-2xl p-8 hover:border-[#C9A962]/30 transition-all duration-300 hover:-translate-y-1">
              <div className="w-12 h-12 bg-[#C9A962]/10 rounded-xl flex items-center justify-center mb-6">
                <Target size={24} className="text-[#C9A962]" />
              </div>
              <h3 className="heading-3 mb-3">Message-Driven Performance</h3>
              <p className="body-small">
                Every read is crafted to ensure your message is felt, understood, and remembered.
              </p>
            </div>

            {/* Card 4 */}
            <div className="bg-[#161616] border border-[#1A1A1A] rounded-2xl p-8 hover:border-[#C9A962]/30 transition-all duration-300 hover:-translate-y-1">
              <div className="w-12 h-12 bg-[#C9A962]/10 rounded-xl flex items-center justify-center mb-6">
                <Zap size={24} className="text-[#C9A962]" />
              </div>
              <h3 className="heading-3 mb-3">Studio-Quality Audio + Reliable Turnaround</h3>
              <p className="body-small">
                Professional sound, clean production, and dependable delivery — even under tight deadlines.
              </p>
            </div>

            {/* Card 5 */}
            <div className="bg-[#161616] border border-[#1A1A1A] rounded-2xl p-8 hover:border-[#C9A962]/30 transition-all duration-300 hover:-translate-y-1">
              <div className="w-12 h-12 bg-[#C9A962]/10 rounded-xl flex items-center justify-center mb-6">
                <Handshake size={24} className="text-[#C9A962]" />
              </div>
              <h3 className="heading-3 mb-3">Easy to Direct, Easy to Work With</h3>
              <p className="body-small">
                Responsive, collaborative, and committed to refining each project until it matches your vision.
              </p>
            </div>

            {/* Card 6 */}
            <div className="bg-[#161616] border border-[#1A1A1A] rounded-2xl p-8 hover:border-[#C9A962]/30 transition-all duration-300 hover:-translate-y-1">
              <div className="w-12 h-12 bg-[#C9A962]/10 rounded-xl flex items-center justify-center mb-6">
                <Globe2 size={24} className="text-[#C9A962]" />
              </div>
              <h3 className="heading-3 mb-3">Trusted Across Formats and Audiences</h3>
              <p className="body-small">
                Commercials, corporate narration, e-learning, promos, and digital content — 
                delivering voiceovers that build trust and drive engagement.
              </p>
            </div>
          </div>

          {/* Strategic Partner Highlight */}
          <div className="mt-12 bg-gradient-to-r from-[#C9A962]/10 via-[#161616] to-[#C9A962]/10 border border-[#C9A962]/20 rounded-2xl p-8 md:p-12">
            <div className="flex flex-col md:flex-row items-center gap-6 md:gap-8">
              <div className="w-16 h-16 bg-[#C9A962]/20 rounded-2xl flex items-center justify-center flex-shrink-0">
                <Lightbulb size={32} className="text-[#C9A962]" />
              </div>
              <div className="text-center md:text-left">
                <h3 className="heading-2 mb-3">A Strategic Voice Partner, Not Just Talent</h3>
                <p className="body-medium text-[#B8B8B8]">
                  Joy combines vocal skill, technical quality, and communication intelligence 
                  to turn scripts into powerful brand experiences.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Demos Section */}
      <section className="py-24 bg-[#0D0D0D]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#C9A962] text-sm uppercase tracking-widest">Portfolio</span>
            <h2 className="heading-1 mt-4">Featured Demos</h2>
            <p className="body-large mt-4 max-w-2xl mx-auto">
              A curated selection of professional voiceover work
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {featuredDemos.slice(0, 4).map((demo) => (
              <AudioPlayer
                key={demo.id}
                title={demo.title}
                category={demo.category}
                duration={demo.duration}
                description={demo.description}
                audioUrl={demo.audioUrl}
              />
            ))}
          </div>

          <p className="text-center text-[#505050] text-sm mt-8">
            More demos available on the Demos page
          </p>

          <div className="text-center mt-8">
            <Link
              to="/demos"
              className="inline-flex items-center gap-2 px-6 py-3 border border-[#2A2A2A] text-white rounded-full hover:border-[#C9A962] hover:text-[#C9A962] transition-all duration-300"
            >
              View All Demos
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 bg-[#0A0A0A] relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#C9A962]/5 rounded-full blur-[150px] pointer-events-none" />
        
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <span className="text-[#C9A962] text-sm uppercase tracking-widest">Testimonials</span>
            <h2 className="heading-1 mt-4">What Clients Say</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div 
                key={testimonial.id}
                className="bg-[#161616] border border-[#1A1A1A] rounded-2xl p-8 relative hover:border-[#C9A962]/20 transition-all duration-300"
              >
                {/* Quote icon */}
                <div className="absolute -top-4 left-8">
                  <div className="w-8 h-8 bg-[#C9A962] rounded-full flex items-center justify-center">
                    <Quote size={16} className="text-[#0D0D0D]" />
                  </div>
                </div>
                
                {/* Stars */}
                <div className="flex gap-1 mb-4 mt-2">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={14} className="text-[#C9A962] fill-[#C9A962]" />
                  ))}
                </div>
                
                <p className="text-[#B8B8B8] text-sm leading-relaxed mb-6 italic">
                  "{testimonial.quote}"
                </p>
                
                <div className="border-t border-[#2A2A2A] pt-4">
                  <p className="text-white font-medium">{testimonial.name}</p>
                  <p className="text-[#707070] text-sm">{testimonial.role}</p>
                  <p className="text-[#C9A962] text-xs mt-1">{testimonial.company}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-24 bg-[#0D0D0D] border-t border-[#1A1A1A] relative">
        {/* Glow effect */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1/2 h-32 bg-[#C9A962]/10 blur-[100px] pointer-events-none" />
        
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center relative z-10">
          <h2 className="heading-1 mb-6">
            Ready to Bring Your Script to Life?
          </h2>
          <p className="body-large mb-12 max-w-xl mx-auto">
            Let's create something memorable together. Get in touch to discuss 
            your project or request a custom quote.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#C9A962] text-[#0D0D0D] font-semibold rounded-full hover:bg-[#E5D4A1] transition-all duration-300 hover:shadow-[0_0_30px_rgba(201,169,98,0.3)]"
            >
              Request a Quote
              <ArrowRight size={18} />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-8 py-4 border border-[#2A2A2A] text-white font-medium rounded-full hover:border-[#C9A962] hover:text-[#C9A962] transition-all duration-300"
            >
              Book a Session
            </Link>
          </div>
        </div>
      </section>

      {/* Floating CTA Button */}
      <Link
        to="/contact"
        className="fixed bottom-8 right-8 z-50 px-6 py-3 bg-[#C9A962] text-[#0D0D0D] font-semibold rounded-full shadow-lg hover:bg-[#E5D4A1] transition-all duration-300 hover:shadow-[0_0_30px_rgba(201,169,98,0.4)] hidden md:flex items-center gap-2"
      >
        <Mic2 size={18} />
        Get a Quote
      </Link>
    </div>
  );
};

export default HomePage;
